import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import PlannedWorkoutPlan from '../components/PlannedWorkoutPlan';
import { hasDisplayValue } from '../lib/utils';

type PlannedWorkout = {
  title?: string;
  type: string;
  distance?: string;
  pace_goal?: string;
  total_time?: string;
  intervals?: string;
  details?: string;
  intention?: string;
};

const splitMetricValue = (value: unknown, fallbackUnit: string) => {
  if (!hasDisplayValue(value))
    return { value: '—', unit: fallbackUnit.toUpperCase() };

  const display = String(value).trim();
  const match = display.match(/^([\d:.]+)\s*(.*)$/);

  if (!match) return { value: display, unit: fallbackUnit.toUpperCase() };

  return {
    value: match[1],
    unit: (match[2] || fallbackUnit).toUpperCase(),
  };
};

const durationMetricValue = (value: unknown) => {
  if (!hasDisplayValue(value)) return '—';
  return String(value).match(/\d+(?:[.:]\d+)*/)?.[0] || '—';
};

export default function SharedPlannedWorkout() {
  const { token } = useParams({ from: '/shared/planned-workouts/$token' });
  const workout = useQuery<PlannedWorkout>({
    queryKey: ['shared-planned-workout', token],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/shared/planned-workouts/${encodeURIComponent(token)}`,
      );
      if (!response.ok) throw new Error('Shared planned workout not found');
      return (await response.json()).workout;
    },
  });

  if (workout.isLoading)
    return (
      <div className="surface-card mx-auto m-6 max-w-md p-10 text-center text-brand-slate">
        Loading planned workout...
      </div>
    );
  if (!workout.data || workout.error)
    return (
      <div className="surface-card mx-auto m-6 max-w-md p-10 text-center text-brand-slate">
        This share link is not available.
      </div>
    );
  const data = workout.data;
  const isStrengthWorkout = String(data.type || '')
    .toLowerCase()
    .includes('strength');
  const distanceMetric = splitMetricValue(data.distance, 'mi');
  const duration = durationMetricValue(data.total_time);

  return (
    <main className="mx-auto max-w-4xl space-y-6 p-4 pb-12">
      <header>
        <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-brand-slate">
          Shared planned workout
        </p>
        <h1 className="text-3xl font-bold text-brand-midnight">
          {data.title || data.type}
        </h1>
        <span className="mt-3 inline-flex rounded-full bg-brand-midnight px-3 py-1 text-sm font-semibold capitalize text-white">
          {data.type}
        </span>
      </header>
      <section
        className={`${isStrengthWorkout ? 'grid-cols-1' : 'grid-cols-2'} workout-stat-grid surface-card grid gap-4 p-6`}
      >
        {isStrengthWorkout ? null : (
          <div className="text-center">
            <div className="mb-1 text-xs text-gray-500">Distance</div>
            <div className="text-2xl font-black text-gray-900">
              {distanceMetric.value}
            </div>
            <div className="mt-0.5 text-[11px] font-black uppercase tracking-wide text-gray-500">
              {distanceMetric.unit}
            </div>
          </div>
        )}
        <div className="text-center">
          <div className="mb-1 text-xs text-gray-500">Duration</div>
          <div className="text-2xl font-black text-gray-900">{duration}</div>
          <div className="mt-0.5 text-[11px] font-black uppercase tracking-wide text-gray-500">
            MINS
          </div>
        </div>
      </section>

      <PlannedWorkoutPlan
        details={data.details || data.intention}
        intervals={data.intervals}
      />
    </main>
  );
}
